import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { contactService } from '@/services/contactService';
import { CACHE_DURATION } from '../utils/cache';

// ─── Types ──────────────────────────────────────────────────

/**
 * UI-ready contact view. Maps raw API Contact fields to
 * presentation-friendly names used across dashboard pages.
 */
export interface ContactView {
  id: string;
  clientId?: string | null;
  name: string;
  email: string;
  phone?: string;
  source?: 'widget' | 'email' | 'manual' | string;
  notes?: string;
  firstContact: string;
  lastContact: string;
  totalMessages: number;
  status: 'chat' | 'email' | 'manual';
  conversationId?: string;
  latestEmailId?: string;
}

// ─── State ──────────────────────────────────────────────────

interface ContactState {
  contacts: ContactView[];
  currentContact: ContactView | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: ContactState = {
  contacts: [],
  currentContact: null,
  isLoading: false,
  isSaving: false,
  error: null,
  lastFetched: null,
};

// ─── Helpers ────────────────────────────────────────────────

const getContactStatus = (source: string): 'chat' | 'email' | 'manual' => {
  if (source === 'email') return 'email';
  if (source === 'manual') return 'manual';
  return 'chat';
};

const toIsoString = (value: unknown, fallback: Date = new Date()): string => {
  if (value && typeof value === 'object') {
    if ('toDate' in value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
      const dateValue = (value as { toDate: () => Date }).toDate();
      if (!Number.isNaN(dateValue.getTime())) {
        return dateValue.toISOString();
      }
    }

    const timestampValue = value as { seconds?: number };
    const legacySeconds = Reflect.get(value as object, '_seconds');
    const timestampSeconds =
      timestampValue.seconds ?? (typeof legacySeconds === 'number' ? legacySeconds : undefined);
    if (typeof timestampSeconds === 'number') {
      return new Date(timestampSeconds * 1000).toISOString();
    }
  }

  const parsed = value instanceof Date ? value : new Date((value as string | number) ?? fallback);
  if (Number.isNaN(parsed.getTime())) {
    return fallback.toISOString();
  }
  return parsed.toISOString();
};

const mapApiContact = (c: any): ContactView => ({
  id: c.id,
  clientId: c.clientId ?? null,
  name: c.name || 'Anonymous',
  email: c.email || '',
  phone: c.phone,
  source: c.source || 'widget',
  notes: typeof c.notes === 'string' ? c.notes : '',
  firstContact: toIsoString(c.firstSeenAt || c.createdAt),
  lastContact: toIsoString(c.lastSeenAt || c.updatedAt),
  totalMessages: c.conversationCount || 0,
  status: getContactStatus(c.source || 'widget'),
  conversationId: c.latestConversationId || c.metadata?.latestConversationId,
  latestEmailId: c.metadata?.latestEmailId,
});

// ─── Async Thunks ───────────────────────────────────────────

export const fetchContacts = createAsyncThunk(
  'contacts/fetchAll',
  async (options: { force?: boolean } | undefined, { getState, rejectWithValue }) => {
    const force = options?.force ?? false;
    const state = (getState() as { contacts: ContactState }).contacts;
    const now = Date.now();

    // Return cached data if recent and not forced
    if (
      !force &&
      state.contacts.length > 0 &&
      state.lastFetched &&
      now - state.lastFetched < CACHE_DURATION
    ) {
      return state.contacts;
    }

    try {
      const response = await contactService.getContacts({ pageSize: 100 });
      const apiContacts = Array.isArray(response) ? response : response?.data || [];
      const mapped = apiContacts.map(mapApiContact);
      mapped.sort((a, b) => Date.parse(b.lastContact) - Date.parse(a.lastContact));
      return mapped;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to load contacts');
    }
  }
);

export const createContact = createAsyncThunk(
  'contacts/create',
  async (
    data: { name: string; email: string; phone?: string; notes?: string },
    { rejectWithValue }
  ) => {
    try {
      const result = await contactService.createManualContact(data);
      return mapApiContact(result.data ?? result);
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to create contact');
    }
  }
);

export const updateContactThunk = createAsyncThunk(
  'contacts/update',
  async ({ id, data }: { id: string; data: Partial<ContactView> }, { rejectWithValue }) => {
    try {
      const result = await contactService.updateContact(id, data);
      return mapApiContact(result);
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to update contact');
    }
  }
);

export const deleteContactThunk = createAsyncThunk(
  'contacts/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await contactService.deleteContact(id);
      return id;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to delete contact');
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────

const contactSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    setCurrentContact: (state, action: PayloadAction<ContactView | null>) => {
      state.currentContact = action.payload;
    },
    clearContacts: state => {
      state.contacts = [];
      state.currentContact = null;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: builder => {
    // fetchContacts
    builder
      .addCase(fetchContacts.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.contacts = action.payload;
        state.isLoading = false;
        state.lastFetched = Date.now();
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) ?? 'Failed to load contacts';
      });

    // createContact
    builder
      .addCase(createContact.pending, state => {
        state.isSaving = true;
      })
      .addCase(createContact.fulfilled, (state, action) => {
        state.contacts.unshift(action.payload);
        state.isSaving = false;
      })
      .addCase(createContact.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to create contact';
      });

    // updateContactThunk
    builder
      .addCase(updateContactThunk.pending, state => {
        state.isSaving = true;
      })
      .addCase(updateContactThunk.fulfilled, (state, action) => {
        const idx = state.contacts.findIndex(c => c.id === action.payload.id);
        if (idx !== -1) state.contacts[idx] = action.payload;
        if (state.currentContact?.id === action.payload.id) {
          state.currentContact = action.payload;
        }
        state.isSaving = false;
      })
      .addCase(updateContactThunk.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to update contact';
      });

    // deleteContactThunk
    builder
      .addCase(deleteContactThunk.pending, state => {
        state.isSaving = true;
      })
      .addCase(deleteContactThunk.fulfilled, (state, action) => {
        state.contacts = state.contacts.filter(c => c.id !== action.payload);
        if (state.currentContact?.id === action.payload) {
          state.currentContact = null;
        }
        state.isSaving = false;
      })
      .addCase(deleteContactThunk.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to delete contact';
      });
  },
});

export const { setCurrentContact, clearContacts } = contactSlice.actions;
export default contactSlice.reducer;
